import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core'

import { RaporttiType } from '@lemon/core-firebase/model/reports-shared'
import {
  Kirjanpitotili,
  RaporttiRequest,
  RaporttiVirallinenTuloslaskelmaData,
  RaporttiVirallinenTuloslaskelmaDataResponse,
  RaporttiVirallinenTuloslaskelmarivi,
} from 'app/_jaettu-lemonator/model/kirjanpito'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import { startWith, switchMap, tap } from 'rxjs/operators'
import { KlikattuKirjaus, RaporttienHakuvaihtoehdot } from '../raportit.component'

import { Timestamp } from '@lemon/core/model/common'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { KirjanpitoRaportitVertailuTiedotService } from '../../_angular/service/kirjanpito/kirjanpito-raportit-vertailutiedot.service'

@Component({
  selector: '[app-kirjanpito-tuloslaskelma-virallinen]',
  templateUrl: './tuloslaskelma-virallinen.component.html',
  styleUrls: ['./tuloslaskelma-virallinen.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KirjanpitoRaportitTuloslaskelmaVirallinenComponent implements OnInit, OnDestroy {
  @Input() hakuvaihtoehdotObservable: Observable<RaporttienHakuvaihtoehdot>
  @Input() tilitMapObservable: Observable<Map<string, Kirjanpitotili>>
  @Input() paivitaArvotHiljaisestiSubject: BehaviorSubject<number>
  @Input() tulosEnsin: boolean

  @Output() kirjaustaKlikattiin: EventEmitter<KlikattuKirjaus> = new EventEmitter()
  @Output() taseEnsinClicked: EventEmitter<boolean> = new EventEmitter()

  readonly lastSucessfullyUpdated = signal<Timestamp>(undefined)

  readonly lataa = signal(true)
  tuloslaskelmanDataObservable: Observable<RaporttiVirallinenTuloslaskelmaData>
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _firebase: FirebaseLemonator,
    private _asiakasService: AsiakasService,
    private _timestampService: TimestampService,
    private _errorHandler: ErrorHandler,
    private _kirjanpitoRaportitVertailuTiedotService: KirjanpitoRaportitVertailuTiedotService,
  ) {}

  ngOnInit() {
    this._kirjanpitoRaportitVertailuTiedotService.setVertailuTiedotOsatilikaudenMukaan()

    this.tuloslaskelmanDataObservable = combineLatest([
      this._asiakasService.nykyinenAsiakasObservable,
      this.hakuvaihtoehdotObservable.pipe(
        tap(() => {
          this.lataa.set(true)
        }),
      ),
      this._kirjanpitoRaportitVertailuTiedotService.vertailuTiedotAjanJaksoObservable,
      this.paivitaArvotHiljaisestiSubject,
    ]).pipe(
      switchMap(([asiakas, hakuvaihtoehdot, vertailutiedotAjanjakso, paivita]) => {
        if (!hakuvaihtoehdot?.alkaa || !hakuvaihtoehdot?.loppuu || !asiakas) {
          return of<RaporttiVirallinenTuloslaskelmaData>(null)
        }

        const haku: RaporttiRequest = {
          a: asiakas.avain,
          k: 'fi',
          w: RaporttiType.TULOS_VIRALLINEN,
          s: hakuvaihtoehdot.alkaa,
          e: hakuvaihtoehdot.loppuu,
          datasource: hakuvaihtoehdot.datasource,
        }

        if (vertailutiedotAjanjakso === 'koko-tilikausi') {
          haku.fw = 1
        }

        // if (hakuvaihtoehdot.tilista) { haku.b = hakuvaihtoehdot.tilista }
        // if (hakuvaihtoehdot.tiliin) { haku.c = hakuvaihtoehdot.tiliin }
        // if (hakuvaihtoehdot.vapaasanahaku?.trim()) { haku.t = hakuvaihtoehdot.vapaasanahaku.trim() }
        if (hakuvaihtoehdot.projekti) {
          haku.p = hakuvaihtoehdot.projekti
        }

        return this._firebase
          .functionsCall<RaporttiRequest, RaporttiVirallinenTuloslaskelmaDataResponse>('kirjanpitoRaportitData', haku)
          .then(res => {
            if (res.e) {
              throw new Error(res.e)
            }
            this.lastSucessfullyUpdated.set(this._timestampService.now())
            this.lataa.set(false)
            console.log('onnistui')
            return res.data
          })
          .catch(err => {
            console.error('Failed to fetch report data', err)
            this._errorHandler.handleError(err)
          })
      }),
      startWith<RaporttiVirallinenTuloslaskelmaData>({
        c1Header: null,
        c2Header: null,
        c: 'c2',
        data: [],
      }),
    )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  focusOnKirjaus(kirjausnumero: string) {
    this.kirjaustaKlikattiin.emit({ kirjausnumero: kirjausnumero })
  }

  trackAccountRowByAccountNumberFn(index: number, item: RaporttiVirallinenTuloslaskelmarivi) {
    return index
  }

  async vertailuTiedotKokoTilikaudenMukaan() {
    this._kirjanpitoRaportitVertailuTiedotService.setVertailuTiedotKokoTiliKaudenMukaan()
  }

  async vertailuTiedotAjanjaksonMukaan() {
    this._kirjanpitoRaportitVertailuTiedotService.setVertailuTiedotOsatilikaudenMukaan()
  }
}
